import { Dateish, dateTimeShortMonthString, validate } from "src/js/util/dates";
import { formatDistanceToNowStrict } from "date-fns";

export type RelativeLastOpenedKey = "just_now" | "minutes" | "hours" | "yesterday" | "days" | "weeks" | "months";

export type RelativeLastOpenedInfo = {
  key: RelativeLastOpenedKey;
  count?: number;
  exact: string;
};

export const RELATIVE_LAST_OPENED_TRANSLATION_KEY_BASE = "projects.last_opened.relative";

type GetRelativeLastOpenedInfoOptions = {
  fallbackDate?: Dateish;
  now?: Dateish;
};

type DistanceUnit = "second" | "minute" | "hour" | "day" | "week" | "month" | "year";

const RELATIVE_KEY_BY_UNIT: Record<Exclude<DistanceUnit, "year">, RelativeLastOpenedKey> = {
  second: "just_now",
  minute: "minutes",
  hour: "hours",
  day: "days",
  week: "weeks",
  month: "months",
};

export const getRelativeLastOpenedInfo = (
  date?: Dateish | null,
  options?: GetRelativeLastOpenedInfoOptions
): RelativeLastOpenedInfo => {
  const now = validate(options?.now ?? new Date());
  const referenceDate = clampToNow(validate(date ?? options?.fallbackDate ?? now), now);
  const exact = dateTimeShortMonthString(referenceDate);
  const { value, unit } = getDistanceValueAndUnit(referenceDate);

  if (unit === "second" || value === 0) {
    return { key: "just_now", exact };
  }

  if (unit === "day" && value === 1) {
    return { key: "yesterday", exact };
  }

  if (unit === "year") {
    return { key: "months", count: value * 12, exact };
  }

  const key = RELATIVE_KEY_BY_UNIT[unit] ?? "days";

  return {
    key,
    count: key === "just_now" || key === "yesterday" ? undefined : value,
    exact,
  };
};

const clampToNow = (date: Date, now: Date) => (date > now ? now : date);

const getDistanceValueAndUnit = (referenceDate: Date) => {
  const [value, unit] = formatDistanceToNowStrict(referenceDate, {
    roundingMethod: "floor",
    addSuffix: false,
  }).split(" ");

  return {
    value: Math.max(Number(value), 0),
    unit: (unit?.replace(/s$/, "") as DistanceUnit) ?? "day",
  };
};
